import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../coupons/Coupon.css';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import Swal from "sweetalert2";
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { fireAudit } from '../utils/auditClient';

const CouponList = () => {
  const { userData } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [coupons, setCoupons] = useState([]);
  const token = localStorage.getItem('auth-token');
  const [sortConfig, setSortConfig] = useState({
    key: 'startTime',
    direction: 'desc',
    numericColumns: ['minOrderAmount', 'maxDiscount', 'maxUsagePerUser', 'percentage'],
    dateColumns: ['startTime', 'endTime']
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [action, setAction] = useState('');
  const [formData, setFormData] = useState({ title: '', couponCode: '', minOrderAmount: '', maxDiscount: '', maxUsagePerUser: '', percentage: '', startTime: '', endTime: '', description: '' });
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refreshData = () => {
    setLoading(true);
    setError('');
    let apiUrl = process.env.REACT_APP_API_COUPONS_LIST;

    if (userData?.userType === 'ACADEMY' && userData.academyIds?.length > 0) {
      const ids = userData.academyIds.join(',');
      apiUrl += `?academyIds=${ids}`;
    }

    fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          const fetchedCoupons = data.data.coupons.map((coupon) => ({
            id: coupon.id,
            title: coupon.title,
            couponCode: coupon.couponCode,
            minOrderAmount: coupon.minOrderAmount,
            maxDiscount: coupon.maxDiscountAmount,
            maxUsagePerUser: coupon.maxUsagePerUser,
            percentage: coupon.percentage,
            startTime: new Date(coupon.startTime).toISOString().split('T')[0],
            endTime: new Date(coupon.endTime).toISOString().split('T')[0],
            description: coupon.description,
            forAllUsers: coupon.forAllUsers,
            mobileNumbers: coupon.mobileNumbers,
          }));
          setCoupons(fetchedCoupons);
        } else {
          setError('Failed to fetch coupons.');
        }
      })
      .catch((error) => {
        setError('Error fetching data: ' + error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useEffect(() => {
    refreshData();
  }, []);


  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ ...sortConfig, key, direction });
  };
  const sortData = (data) => {
    const { key, direction, numericColumns, dateColumns } = sortConfig;
    if (!key) return data;

    return [...data].sort((a, b) => {
      if (numericColumns.includes(key)) {
        const numA = parseFloat(a[key]) || 0;
        const numB = parseFloat(b[key]) || 0;
        return direction === 'asc' ? numA - numB : numB - numA;
      }

      if (dateColumns.includes(key)) {
        const dateA = new Date(a[key]).getTime();
        const dateB = new Date(b[key]).getTime();
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      }

      const valueA = String(a[key] || '').toLowerCase();
      const valueB = String(b[key] || '').toLowerCase();

      if (valueA < valueB) return direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const [dropdownOpen, setDropdownOpen] = useState(null);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleCreateCoupon = (event) => {
    const url = `/newCoupon`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handleEditCoupon = (event, couponId) => {
    const url = `/newCoupon?couponId=${couponId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };


  const handleDeleteCoupon = (couponId) => {
    const coupon = coupons.find(coupon => coupon.id === couponId);
    setSelectedCoupon(coupon);
    setAction('delete');
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const endpoint = `${process.env.REACT_APP_API_COUPONS_REMOVE}?coupon_id=${selectedCoupon.id}&user_type=admin`;
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorDetails = await response.json();
        throw new Error(`Failed to delete coupon: ${errorDetails.message || response.statusText}`);
      }

      const updatedCoupons = coupons.filter(coupon => coupon.id !== selectedCoupon.id);
      setCoupons(updatedCoupons);
      setIsModalOpen(false);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Coupon deleted successfully.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (error) {

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const cancelAction = () => {
    setIsModalOpen(false);
  };

  const filteredAndSortedCoupons = sortData(
    coupons.filter((coupon) =>
      Object.values(coupon)
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
  );


  const totalPages = Math.ceil(filteredAndSortedCoupons.length / itemsPerPage);
  const displayedCoupons = filteredAndSortedCoupons.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen !== null && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownOpen]);

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  useEffect(() => {
    const closeOnScroll = () => setDropdownOpen(null);

    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, []);

  const totalEntries = filteredAndSortedCoupons.length;
  const indexOfFirstEntry = (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastEntry = Math.min(currentPage * itemsPerPage, totalEntries);

  return (
    <div className="enrollment-container">
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        {hasPrivilege(userData, 'COUPONS', 'COUPONS_LIST_MODIFY') && (
          <button
            className="create-button-new"
            onClick={handleCreateCoupon}
          >
            <span className="create-button__icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="23"
                height="23"
                className="create-button__svg"
              >
                <path
                  d="M12 5v14M5 12h14"
                  stroke="white"
                  strokeWidth="2.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="create-button__label">
              Create Coupon
            </span>
          </button>
        )}
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={itemsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${itemsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={refreshData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredAndSortedCoupons.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="tt-table-container">
        <table className="tt-table">
          <thead>
            <tr>
              {[
                { key: 'title', label: 'Title' },
                { key: 'couponCode', label: 'Coupon Code' },
                { key: 'minOrderAmount', label: 'Min Order Amount' },
                { key: 'maxDiscount', label: 'Max Discount' },
                { key: 'maxUsagePerUser', label: 'Max Usage Per User' },
                { key: 'percentage', label: 'Percentage' },
                { key: 'startTime', label: 'Start Time' },
                { key: 'endTime', label: 'End Time' },
                { key: 'description', label: 'Description' }
              ].map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                        } ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
              {hasPrivilege(userData, 'COUPONS', 'COUPONS_LIST_MODIFY') && <th>Action</th>}
            </tr>
          </thead>

          <tbody>
            {displayedCoupons.map((coupon) => (
              <tr key={coupon.id}>
                <td>{coupon.title}</td>
                <td>{coupon.couponCode}</td>
                <td>{coupon.minOrderAmount}</td>
                <td>{coupon.maxDiscount}</td>
                <td>{coupon.maxUsagePerUser}</td>
                <td>{coupon.percentage}%</td>
                <td>{coupon.startTime}</td>
                <td>{coupon.endTime}</td>
                <td>{coupon.description}</td>
                {hasPrivilege(userData, 'COUPONS', 'COUPONS_LIST_MODIFY') && (
                  <td className="action-buttons" style={{ borderBottom: 'none' }}>
                    <button
                      className="edit-btn tooltip-btn"
                      onClick={(event) => handleEditCoupon(event, coupon.id)}
                      title="Edit"
                    >

                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="white"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z"
                        />
                      </svg>
                      <span className="tooltip-text">Edit</span>
                    </button>

                    <button
                      className="delete-btn tooltip-btn"
                      onClick={() => handleDeleteCoupon(coupon.id)}
                      title="Disable"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="white"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z"
                        />
                      </svg>
                      <span className="tooltip-text">Delete</span>
                    </button>
                  </td>
                )}


              </tr>
            ))}
          </tbody>

        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-btn" onClick={cancelAction}>&times;</button>
            <div>
              <p>
                Are you sure you want to delete the coupon: {selectedCoupon.title}?
              </p>
              <div className="modal-actions">
                <button className="confirm-btn" onClick={confirmDelete}>
                  Confirm Disable
                </button>
                <button className="cancel-btn" onClick={cancelAction}>
                  Cancel
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default CouponList;
